import { button, element, heading } from "@groundspark/browser-ui";
import type { Leaflet } from "./domain";
import { destinationField, qrDestination } from "./leaflet-destination";

/**
 * Shows where the leaflet's QR code leads, and saves a new destination. Saving
 * errors stay in the dialog; aborting the signal closes it. Resolves with the
 * saved leaflet, or `null` when nothing changed.
 */
export function leafletSettingsDialog(
  leaflet: Leaflet,
  save: (destination: string) => Promise<Leaflet>,
  signal: AbortSignal,
): Promise<Leaflet | null> {
  const dialog = element("dialog", "dialog");
  const title = heading(2, "Leaflet settings", "dialog__title");
  title.id = "leaflet-settings-title";
  dialog.setAttribute("aria-labelledby", title.id);
  const destination = destinationField(leaflet.qrCode.destination);
  // Shown as text rather than a link: opening it would count a scan.
  const link = element(
    "div",
    "field",
    element("span", "field__label", "QR code link"),
    element("code", "dialog__description", leaflet.qrCode.url),
    element("span", "muted", "This is what the QR code holds. It never changes."),
  );
  const error = element("p", "composer__error");
  error.setAttribute("role", "alert");
  const cancel = button("Cancel", "button button--white", () => dialog.close());
  const submit = element("button", "button button--dark-blue", "Save settings");
  submit.type = "submit";
  const form = element(
    "form",
    null,
    destination.label,
    link,
    error,
    element("div", "dialog__actions", cancel, submit),
  );
  dialog.append(
    element("header", "dialog__header", title, element("p", "muted", leaflet.name)),
    form,
  );
  const previousFocus = document.activeElement;
  let saving = false;
  let saved: Leaflet | null = null;
  return new Promise((resolve) => {
    const abort = (): void => dialog.close();
    signal.addEventListener("abort", abort, { once: true });
    dialog.addEventListener("cancel", (event) => {
      if (saving) event.preventDefault();
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (saving) return;
      let address: string;
      try {
        address = qrDestination(destination.input.value);
      } catch (failure) {
        error.textContent = failure instanceof Error ? failure.message : String(failure);
        destination.input.focus();
        return;
      }
      if (address === leaflet.qrCode.destination) {
        dialog.close();
        return;
      }
      saving = true;
      submit.disabled = true;
      cancel.disabled = true;
      error.textContent = "";
      void save(address)
        .then((leaflet) => {
          saved = leaflet;
          dialog.close();
        })
        .catch((failure: unknown) => {
          error.textContent = failure instanceof Error ? failure.message : String(failure);
        })
        .finally(() => {
          saving = false;
          submit.disabled = false;
          cancel.disabled = false;
        });
    });
    dialog.addEventListener(
      "close",
      () => {
        signal.removeEventListener("abort", abort);
        dialog.remove();
        if (previousFocus instanceof HTMLElement && previousFocus.isConnected)
          previousFocus.focus({ preventScroll: true });
        resolve(saved);
      },
      { once: true },
    );
    document.body.append(dialog);
    dialog.showModal();
    destination.input.focus();
  });
}
